/**
 * The verbs a spoken partial may ACT on before the sentence ends (spec
 * 2026-09-22 §7, R-N3): no sensitivity. Everything else waits for the settled
 * sentence. Not every member has an undo (`system.media_key` has none): the
 * inverses live in `osVerbInverseOf`, where one exists.
 */
import { LAUNCHABLE_APPS } from './os-verbs.js';

/**
 * What a partial may ACT on, and nothing else: every member is `sensitivity: 'none'`.
 * Inverses do not live here, they live in `osVerbInverseOf` — `sysevents.app_hide` is the
 * undo of a launch and is never itself an early act, so it is absent from this set and a
 * caller must never gate an inverse on membership. Widening this set is a product decision,
 * so os-verb-early-set.test.ts pins its exact contents.
 */
export const EARLY_VERBS: ReadonlySet<string> = new Set([
  'sysevents.app_launch',
  'system.mute_set',
  'system.media_key',
]);

/** The System Events process name app_hide addresses, which is not always the name people say. */
const processFor = (bundleId: unknown): string | null => {
  const app = LAUNCHABLE_APPS.find((a) => a.bundleId === bundleId);
  return app ? (app.process ?? app.name) : null;
};

/**
 * The verb that undoes `verb` with `params`, or null when there is none to run.
 *
 * An inverse may itself be a 'normal' verb (app_hide rides the per-app grant elsewhere).
 * The stream reflex runs an inverse only against the app it opened itself seconds earlier,
 * through the same relay and behind the same hands kill switch; it never checks EARLY_VERBS
 * for an inverse, only for an act.
 */
export function osVerbInverseOf(verb: string, params: Record<string, unknown>): { verb: string; params: Record<string, unknown> } | null {
  switch (verb) {
    case 'sysevents.app_launch': {
      const name = processFor(params.bundleId);
      return name ? { verb: 'sysevents.app_hide', params: { name, hidden: true } } : null;
    }
    case 'sysevents.app_activate':
      return typeof params.name === 'string' ? { verb: 'sysevents.app_hide', params: { name: params.name, hidden: true } } : null;
    case 'sysevents.app_hide':
      return typeof params.name === 'string' && typeof params.hidden === 'boolean' ? { verb: 'sysevents.app_hide', params: { name: params.name, hidden: !params.hidden } } : null;
    case 'system.mute_set':
      return typeof params.muted === 'boolean' ? { verb: 'system.mute_set', params: { muted: !params.muted } } : null;
    default:
      return null;
  }
}
